import { useRef } from "react";
import * as moment from 'moment';
import { toast } from "react-toastify";
import { ColumnApi, ExcelCell, ExcelExportParams, ExcelStyle, GridApi, GridReadyEvent } from "ag-grid-community";

export const excelStyles: ExcelStyle[] = [
    {
        id: 'header',
        alignment: { horizontal: 'Center' },
        font: { bold: true },
        interior: { color: '#ADD8E6', pattern: 'Solid', patternColor: '#ADD8E6' }
    },
    {
        id: 'cell',
        alignment: { horizontal: 'Center' }
    },
    {
        id: 'currency',
        dataType: 'Number',
        numberFormat: { format: '#,##0.00' }
    },
    {
        id: 'exportTitle',
        font: { bold: true, size: 16 }
    },
    {
        id: 'sectionLabel',
        font: { bold: true }
    },
    {
        id: 'totalRowLabel',
        font: { bold: true },
        alignment: { horizontal: 'Center' },
        interior: { color: '#D3D3D3', pattern: 'Solid', patternColor: '#D3D3D3' }
    },
    {
        id: 'totalRowValue',
        dataType: 'Number',
        font: { bold: true },
        alignment: { horizontal: 'Center' },
        numberFormat: { format: '#,##0.00' },
        interior: { color: '#D3D3D3', pattern: 'Solid', patternColor: '#D3D3D3' }
    },
    {
        id: 'headerRow',
        font: { bold: true },
        alignment: { horizontal: 'Center' },
        interior: { color: '#ADD8E6', pattern: 'Solid', patternColor: '#ADD8E6' }
    }
];

export default function useExportPaymentList() {
    const gridApiRef = useRef<GridApi>(null);
    const columnApiRef = useRef<ColumnApi>(null);

    const onGridReady = (e: GridReadyEvent) => {
        gridApiRef.current = e.api;
        columnApiRef.current = e.columnApi;
    };

    const toDataRow = (row: any, index: number): ExcelCell[] => [
        { data: { type: 'Number', value: String(index + 1) }, styleId: 'cell' },
        { data: { type: 'String', value: row.centerAlias }, styleId: 'cell' },
        { data: { type: 'Number', value: String(row.payNow) }, styleId: 'currency' },
    ];

    const exportPaymentList = () => {
        if (!gridApiRef.current || !columnApiRef.current) return;

        if (gridApiRef.current.getSelectedRows().length === 0) {
            toast.error("Please select at least one row to export.");
            return;
        }

        const selectedRows = gridApiRef.current.getSelectedRows();
        const onTimeCenters = selectedRows.filter((r: any) => !r.centerHasLatePaymentsInLastYear);
        const lateCenters = selectedRows.filter((r: any) => r.centerHasLatePaymentsInLastYear);

        const onTimeTotal = onTimeCenters.reduce((sum: number, r: any) => sum + r.payNow, 0);
        const lateTotal = lateCenters.reduce((sum: number, r: any) => sum + r.payNow, 0);
        const total = onTimeTotal + lateTotal;

        const name = moment().format('MMMM YYYY') + ' CCB AP';

        const headerRow: ExcelCell[] = [
            { data: { type: 'String', value: 'R#' }, styleId: 'headerRow' },
            { data: { type: 'String', value: 'Center' }, styleId: 'headerRow' },
            { data: { type: 'String', value: 'Pay Now' }, styleId: 'headerRow' },
        ];

        const toSubTotalRow = (amount: number): ExcelCell[] => [
            { data: { type: 'String', value: '' } },
            { data: { type: 'String', value: 'Sub Total' }, styleId: 'totalRowLabel' },
            { data: { type: 'Number', value: String(amount) }, styleId: 'totalRowValue' },
        ];

        const params: ExcelExportParams = {
            skipColumnHeaders: true,
            shouldRowBeSkipped: () => true,
            columnKeys: ['rank', 'centerAlias', 'payNow'],
            sheetName: name,
            fileName: `${name}.xlsx`,
            prependContent: [
                [
                    { data: { type: 'String', value: '' } },
                    { data: { type: 'String', value: name }, styleId: 'exportTitle', mergeAcross: 1 },
                ],
                [],
                [
                    { data: { type: 'String', value: '' } },
                    { data: { type: 'String', value: 'On Time Centers' }, styleId: 'sectionLabel' },
                ],
                headerRow,
                ...onTimeCenters.map(toDataRow),
                toSubTotalRow(onTimeTotal),
                [],
                [
                    { data: { type: 'String', value: '' } },
                    { data: { type: 'String', value: 'Late Centers' }, styleId: 'sectionLabel' },
                ],
                headerRow,
                ...lateCenters.map(toDataRow),
                toSubTotalRow(lateTotal),
                [],
                [
                    { data: { type: 'String', value: '' } },
                    { data: { type: 'String', value: 'Total' }, styleId: 'totalRowLabel' },
                    { data: { type: 'Number', value: String(total) }, styleId: 'totalRowValue' },
                ],
            ],
        };

        gridApiRef.current.exportDataAsExcel(params);
    };

    return { onGridReady, exportPaymentList };
}
